import { DatePipe } from '@angular/common';
import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { Chart, ChartConfiguration } from "chart.js";
import { ChartType } from 'chart.js/auto';
import zoomPlugin from 'chartjs-plugin-zoom';
import { Subject } from 'rxjs';
import { IHSelectedMetric, NumericType, TimeType } from 'src/app/gamification/command-center/models/games-parameter.model';
import { GameType } from 'src/app/gamification/command-center/models/games.model';
import { GamesParameterHistoryService } from 'src/app/gamification/command-center/services/games-parameter-history.service';
import { GameHistoryHealthReportService, Interval } from '../../../../services/game-history-health-report.service';
import { GamesService } from '../../../../services/games.service';

Chart.register(zoomPlugin);

@Component({
    selector: 'cv-game-history-breakdown-effectiveness',
    templateUrl: './game-history-breakdown-effectiveness.component.html',
    styleUrl: './game-history-breakdown-effectiveness.component.scss'
})
export class GameHistoryBreakdownEffectivenessComponent implements OnInit, OnChanges {
    @Input() game = null;
    @Input() participants = [];
    @Input() intervalSelected: Interval[];
    @Input() isLoadingGameParams: boolean = false;

    public chartOptions: ChartConfiguration['options'] = {
        responsive: true,
        maintainAspectRatio: false,
        animation: {
            duration: 0
        },
        plugins: {
            legend: {
                labels: {
                    filter: (legendItem, chartData) => {
                        // Only shows the label of the first dataset
                        return legendItem.datasetIndex === 0;
                    }
                }
            },
            tooltip: {
                callbacks: {
                    label: (context) => {
                        // For dataset 1 (just points)
                        if (context.datasetIndex === 1) {
                            if (this.game?.GameType === GameType.Brackets && this.game.HistoryBracketTournamentRounds?.length) {
                                const round = this.game.HistoryBracketTournamentRounds.find(r =>
                                    this.formatPlotDate(new Date(r.StartDate)) === context.label ||
                                    this.formatPlotDate(new Date(r.EndDate)) === context.label
                                );

                                if (round) {
                                    return this.getBracketRoundName(round.RoundNumber);
                                }
                                return '';
                            }
                            else if (this.intervalSelected?.[0]) {
                                return context.label === this.formatPlotDate(new Date(this.intervalSelected[0].StartDate))
                                    ? this.getLocalizedText('Period_Start', 'Start Period')
                                    : this.getLocalizedText('Period_End', 'End Period');
                            }
                        }
                        return '';
                    }
                }
            },
            zoom: {
                zoom: {
                    wheel: {
                        enabled: true,
                    },
                    drag: {
                        enabled: false,
                    },
                    pinch: {
                        enabled: true,
                    }
                },
                pan: {
                    enabled: true,
                    mode: 'xy',
                    modifierKey: null,

                },
                limits: {
                    x: { min: 'original', max: 'original' },
                    y: { min: 'original', max: 'original' },
                }
            },
        },
        scales: {
            y: {
                min: 0,
                beginAtZero: false,
                ticks: {
                    callback: (value) => {
                        return this.formatMetricDisplayValue(value as number);
                    },
                },
            },
        },
    };
    public chartType: ChartType = 'line';
    public chartData: ChartConfiguration['data'] = {
        labels: [],
        datasets: [{
            label: '',
            data: [],
            fill: 'origin',
            backgroundColor: '#DC4E78',
            borderColor: '#DC4E78',
            tension: 0.1,
        }],
    };
    public numericType: NumericType = NumericType.Number;
    public timeType: TimeType = TimeType.None;
    intervalAverages = [];

    gameTypeName: string;
    gameIntervalType: string;
    gamePeriod: string;
    previousPeriod: string;
    gamePeriodDates: string[] = [];
    previousPeriodDates: string[] = [];
    isLoading: boolean = true;
    gamePeriodAvg: string = "---";
    noVariationText: string = "";
    previousPeriodAvg: string = "---";
    metricList: IHSelectedMetric[] = [];
    gameMetric: IHSelectedMetric;

    destroy$ = new Subject<void>();

    private readonly CHART_COLORS = {
        backgroundColor: '#DC4E78',
        borderColor: '#DC4E78'
    };

    constructor(
        private gamesService: GamesService,
        private datePipe: DatePipe,
        private gamesParameterHistoryService: GamesParameterHistoryService,
        private gamesHealthReportService: GameHistoryHealthReportService
    ) { }

    ngOnInit(): void { }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.intervalSelected && this.intervalSelected) {
            this.updatePeriodsAndChart();
            this.checkAndLoadMetricRanking();
        }
        if (changes.isLoadingGameParams && !this.isLoadingGameParams) {
            this.getMetrics();
        }
        if (changes.game && this.game) {
            this.chartData = {
                labels: [],
                datasets: [
                    { ...this.chartData.datasets[0], data: [] }, // Dataset principal
                    { ...this.chartData.datasets[1], data: [] }  // Dataset for points
                ]
            };

            if (this.game.IntervalType) {
                this.gameIntervalType = this.game.IntervalType;
                this.updatePeriodsAndChart();
            }
            this.checkAndLoadMetricRanking();
        }
        if (changes.participants && this.participants?.length) {
            this.checkAndLoadMetricRanking();
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    getLocalizedText(key: string, defaultText: string): string {
        return this.gamesHealthReportService.getLocalizationMessage(key, defaultText);
    }

    checkAndLoadMetricRanking(): void {
        if (this.game && this.participants?.length > 0 && this.intervalSelected?.length > 0) {
            this.loadMetricRanking();
        }
    }

    updatePeriodsAndChart(): void {
        if (!this.intervalSelected || this.intervalSelected.length === 0) {
            this.gamePeriod = '---';
            this.previousPeriod = '---';
            this.gamePeriodDates = [];
            this.previousPeriodDates = [];
            this.noVariationText = '';
            return;
        }

        this.updatePreviousPeriod();
        this.updateGamePeriod();
        this.updateChart();
    }

    calculatePeriodAverages(): void {
        if (!this.chartData?.labels || !this.chartData.datasets?.[0]?.data) {
            this.gamePeriodAvg = "---";
            this.previousPeriodAvg = "---";
            this.noVariationText = "";
            return;
        }

        const gamePeriodAvg = this.getPeriodAverage(this.gamePeriodDates);
        this.gamePeriodAvg = this.formatMetricDisplayValue(gamePeriodAvg);

        const previousPeriodAvg = this.getPeriodAverage(this.previousPeriodDates);
        this.previousPeriodAvg = this.formatMetricDisplayValue(previousPeriodAvg);
        this.noVariationText = `(${this.getLocalizedText("No_Variation", "No variation")})`;
    }

    updateGamePeriod(): void {
        this.gamePeriodDates = this.getGamePeriodDates();
        this.gamePeriod = this.getGamePeriod();
    }

    updatePreviousPeriod(): void {
        this.previousPeriodDates = this.getPreviousPeriodDates();
        this.previousPeriod = this.getPreviousPeriod();
    }

    updateChart(): void {
        if (this.isLoading || !this.chartData) {
            return;
        }

        const gamePeriodDates = this.getGamePeriodDates();
        const previousPeriodDates = this.getPreviousPeriodDates();
        const allDates = [...new Set([...previousPeriodDates, ...gamePeriodDates])];
        const values = this.chartData.datasets[0].data;

        this.calculatePeriodAverages();

        this.chartData = {
            labels: allDates,
            datasets: [{
                label: this.getChartDatasetLabel(),
                data: values,
                fill: 'origin',
                backgroundColor: this.CHART_COLORS.backgroundColor,
                borderColor: this.CHART_COLORS.borderColor,
                tension: 0.1,
                order: 2
            },
            {
                label: '',
                data: this.getZeroPositionPoints(allDates),
                pointRadius: 5,
                pointHoverRadius: 8,
                showLine: false,
                order: 1,
            }
            ],
        };
    }

    getZeroPositionPoints(dates: string[]): (number | null)[] {
        const points = new Array(dates.length).fill(null);
        const dateToIndexMap = new Map(dates.map((date, index) => [date, index]));

        if (this.game?.GameType === GameType.Brackets && this.game.HistoryBracketTournamentRounds?.length) {
            this.game.HistoryBracketTournamentRounds.forEach(round => {
                const roundStartDate = this.formatPlotDate(new Date(round.StartDate));

                const startIndex = dateToIndexMap.get(roundStartDate);

                if (startIndex !== undefined) points[startIndex] = 0;
            });
            return points;
        }

        if (!this.intervalSelected?.[0]) return points;

        const startDate = this.formatPlotDate(new Date(this.intervalSelected[0].StartDate));
        const endDateObj = new Date(this.intervalSelected[0].EndDate);
        if (this.game?.IntervalType !== 'Day' || this.game?.GameType === GameType.Brackets) {
            endDateObj.setDate(endDateObj.getDate() - 1);
        }
        const endDate = this.formatPlotDate(endDateObj);

        const startIndex = dateToIndexMap.get(startDate);
        const endIndex = dateToIndexMap.get(endDate);

        if (startIndex !== undefined) points[startIndex] = 0; // Point in Y=0
        if (endIndex !== undefined) points[endIndex] = 0;     // Point in Y=0

        return points;
    }

    async loadMetricRanking(): Promise<void> {
        if (!this.game ||
            !this.participants?.length ||
            !this.gameMetric ||
            !this.intervalSelected?.length) {
            this.isLoading = false;
            this.chartData.datasets[0].data = [];
            return;
        }

        if (!this.intervalSelected[0]?.StartDate || !this.intervalSelected[0]?.EndDate) {
            this.isLoading = false;
            this.chartData.datasets[0].data = [];
            return;
        }

        this.isLoading = true;

        try {
            const previousStartDate = this.getPreviousStartDate();
            const firstPreviousDate = previousStartDate[0];
            const formattedFirstPreviousDate = new Date(firstPreviousDate).toISOString().replace(/\.\d{3}Z$/, '');

            const formattedParticipants = this.participants.map(({ participant }) => ({
                ...participant,
                GroupID: participant.RelatedGroupID
            })) || [];

            const metricID = this.gameMetric.MetricDetail.MetricID.toString();

            if (formattedParticipants.length > 0) {
                let endDateToSend = this.intervalSelected[0].EndDate;
                if (this.game?.IntervalType !== 'Day' || this.game?.GameType === GameType.Brackets) {
                    const adjustedEndDate = new Date(endDateToSend);
                    adjustedEndDate.setDate(adjustedEndDate.getDate() - 1);
                    endDateToSend = adjustedEndDate.toISOString().replace(/\.\d{3}Z$/, '');
                }

                const allInfo = await this.gamesService.LoadMetricRanking(
                    metricID,
                    formattedParticipants,
                    formattedFirstPreviousDate,
                    endDateToSend,
                    true
                );

                this.numericType = allInfo.NumericType;
                this.timeType = allInfo.TimeType;
                this.intervalAverages = allInfo.IntervalAverages;

                // Transform the data for the chart
                const values = this.intervalAverages.map(item => item.AverageValue);
                const labels = this.intervalAverages.map(item => {
                    const date = new Date(item.Date);
                    return this.datePipe.transform(date, 'MM/dd', 'UTC') || '';
                });

                this.chartData = {
                    labels: labels,
                    datasets: [{
                        label: this.getChartDatasetLabel(),
                        data: values,
                        fill: 'origin',
                        backgroundColor: this.CHART_COLORS.backgroundColor,
                        borderColor: this.CHART_COLORS.borderColor,
                        tension: 0.1,
                        order: 2,
                    },
                    {
                        label: '',
                        data: this.getZeroPositionPoints(labels),
                        pointRadius: 5,
                        pointHoverRadius: 8,
                        showLine: false,
                        order: 1,
                    }],
                };

                this.updateGamePeriod();
                this.updatePreviousPeriod();
                this.calculatePeriodAverages();
            }
        } catch (error) { }
        finally {
            this.isLoading = false;
        }
    }

    calculateDifference(): string {
        const currentValue = this.parseNumericValue(this.gamePeriodAvg);
        const previousValue = this.parseNumericValue(this.previousPeriodAvg);
        let difference = currentValue - previousValue;

        if (difference == 0) {
            return this.noVariationText;
        }
        
        const sign = difference >= 0 ? '+ ' : '- ';
        difference = Math.abs(difference);
        if (this.numericType === NumericType.Time && this.timeType === TimeType.Time) {
            return `(${sign}${this.formatMetricTime(difference)})`;
        }

        const displayValue = this.numericType === NumericType.Percent
            ? this.formatPercent(difference / 100)
            : this.formatMetricDisplayValue(difference);
        return `(${sign}${displayValue})`;
    }

    parseNumericValue(value: string): number {
        if (value === '---') return 0;
        return parseFloat(value.replace(/[^0-9.-]+/g, ''));
    }

    getStyleClass(): string {
        const difference = this.parseNumericValue(this.gamePeriodAvg) - this.parseNumericValue(this.previousPeriodAvg);

        if (difference == 0) {
            return 'metricChangeZero';
        }

        const isMetricChangePositive = (this.metricDirection === 'Up' && difference > 0) || (this.metricDirection !== 'Up' && difference < 0);
        return isMetricChangePositive ? 'metricChangePositive' : 'metricChangeNegative';
    }

    formatMetricDisplayValue(value: number): string {
        if (value === null || value === undefined) {
            return '';
        }

        switch (this.numericType) {
            case NumericType.Number:
                return this.formatNumber(value);
            case NumericType.Currency:
                return this.formatCurrency(value);
            case NumericType.Percent:
                return this.formatPercent(value);
            case NumericType.Time:
                if (this.timeType === TimeType.Time) {
                    return this.formatMetricTime(value);
                } else {
                    return this.formatNumber(value);
                }
            default:
                return this.formatNumber(value);
        }
    }

    formatNumber(value: number): string {
        return value.toLocaleString('en-US', {
            minimumFractionDigits: 0,
            maximumFractionDigits: 2
        });
    }

    formatCurrency(value: number): string {
        return value.toLocaleString('en-US', {
            style: 'currency',
            currency: 'USD'
        });
    }

    formatPercent(value: number): string {
        return value.toLocaleString('en-US', {
            style: 'percent',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        });
    }

    formatMetricTime(seconds: number): string {
        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        const remainingSeconds = Math.floor(seconds % 60);

        return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
    }

    getPeriodAverage(dates: string[]): number {
        if (!this.intervalAverages || dates.length === 0) {
            return 0;
        }

        const formattedIntervalAverages = this.intervalAverages.map(item => {
            const [year, month, day] = item.Date.split('T')[0].split('-');
            const date = new Date(Number(year), Number(month) - 1, Number(day));
            return {
                ...item,
                Date: this.datePipe.transform(date, 'MM/dd') || ''
            };
        });

        const valuesInPeriod = formattedIntervalAverages
            .filter(item => dates.includes(item.Date))
            .map(item => item.AverageValue);

        if (valuesInPeriod.length === 0) {
            return 0;
        }

        // Calculate average
        const sum = valuesInPeriod.reduce((acc, value) => acc + value, 0);
        return sum / valuesInPeriod.length;
    }

    getChartDatasetLabel(): string {
        return this.gameMetric && this.gameMetric.MetricDetail?.Abbrev;

    }

    onMetricSelectionChange(): void {
        this.updateChartDatasetLabel();
        this.loadMetricRanking();
    }

    updateChartDatasetLabel(): void {
        if (this.chartData?.datasets?.length > 0) {
            this.chartData.datasets[0].label = this.getChartDatasetLabel();
            this.chartData = { ...this.chartData };
        }
    }

    getGamePeriod(): string {
        if (!this.intervalSelected[0]?.StartDate || !this.intervalSelected[0]?.EndDate) {
            return '---';
        }

        const startDate = new Date(this.intervalSelected[0].StartDate);
        const endDate = new Date(this.intervalSelected[0].EndDate);

        return `${this.formatDate(startDate)} - ${this.formatDate(endDate)}`;
    }

    getMetrics() {
        if (!this.intervalSelected || this.intervalSelected.length === 0 || !this.intervalSelected[0]?.EndDate) {
            return;
        }

        this.metricList = [];

        const endDate = this.intervalSelected[0].EndDate?.split('T')[0];
        const metrics = this.gamesParameterHistoryService.getMetrics(this.game.RuleID, endDate);

        if (this.game?.GameType == GameType.Raffle) {
            this.gameMetric = metrics.length > 0 ? metrics[0] : null
        }
        else if (this.game?.GameType != GameType.Connect3 && this.game?.GameType != GameType.Blackout) {
            this.gameMetric = metrics.length > 0 ? metrics.find(x => !x.IsQualifier) : null;

        } else {
            this.metricList = metrics.filter(x => !x.IsQualifier);
            this.gameMetric = this.metricList[0];
        }
    }


    getPreviousPeriod(): string {
        if (!this.intervalSelected[0]?.StartDate || !this.intervalSelected[0]?.EndDate) {
            return '---';
        }

        const { startDate, endDate } = this.calculatePreviousPeriodDates();
        return `${this.formatDate(startDate)} - ${this.formatDate(endDate)}`;
    }

    getGamePeriodDates(): string[] {
        if (!this.intervalSelected[0]?.StartDate || !this.intervalSelected[0]?.EndDate) {
            return [];
        }

        const startDate = new Date(this.intervalSelected[0].StartDate);
        const endDate = new Date(this.intervalSelected[0].EndDate);

        return this.getDatesInRange(startDate, endDate);
    }

    getPreviousPeriodDates(): string[] {
        if (!this.intervalSelected[0]?.StartDate || !this.intervalSelected[0]?.EndDate) {
            return [];
        }

        const { startDate, endDate } = this.calculatePreviousPeriodDates();
        return this.getDatesInRange(startDate, endDate);
    }

    getPreviousStartDate(): Date[] {
        if (!this.intervalSelected[0]?.StartDate || !this.intervalSelected[0]?.EndDate) {
            return [];
        }

        const { startDate } = this.calculatePreviousPeriodDates();
        return [startDate];
    }

    calculatePreviousPeriodDates(): { startDate: Date; endDate: Date } {
        if (!this.intervalSelected || !this.intervalSelected.length ||
            !this.intervalSelected[0]?.StartDate || !this.intervalSelected[0]?.EndDate) {
            return { startDate: new Date(), endDate: new Date() };
        }

        const startDate = new Date(this.intervalSelected[0].StartDate);
        let endDate = new Date(this.intervalSelected[0].EndDate);

        if (this.game?.GameType == GameType.Brackets) {
            startDate?.setDate(startDate?.getDate() - 7);
            endDate = new Date(startDate);
            endDate.setDate(endDate.getDate() + 6);

            return { startDate, endDate };
        }

        switch (this.gameIntervalType) {
            case 'Day':
                startDate?.setDate(startDate?.getDate() - 1);
                endDate = new Date(startDate);
                endDate.setDate(endDate.getDate() + 1);
                break;
            case 'Week':
                startDate?.setDate(startDate?.getDate() - 7);
                endDate = new Date(startDate);
                endDate.setDate(endDate.getDate() + 6);
                break;
            case 'Month':
                startDate?.setDate(startDate?.getDate() - 14);
                endDate = new Date(startDate);
                endDate.setDate(endDate.getDate() + 13);
                break;
            default:
                break;
        }

        return { startDate, endDate };
    }

    getDatesInRange(startDate: Date, endDate: Date): string[] {
        const dates: string[] = [];
        let currentDate = new Date(startDate);

        while (currentDate <= endDate) {
            dates.push(this.formatPlotDate(currentDate));
            currentDate.setDate(currentDate.getDate() + 1);
        }

        return dates;
    }

    getBracketRoundName(round: number) {
        switch (round) {
            case 1:
                return this.getLocalizedText("Round_1_Final", "Final");
                break;
            case 2:
                return this.getLocalizedText("Round_2_Semi_Finals", "Semi Finals");
                break;
            case 3:
                return this.getLocalizedText("Round_3_Quarter", "Quarters");
                break;
            case 4:
                return this.getLocalizedText("Round_4_Octaves", "Octaves");
                break;
        }
    }

    formatDate(date: Date): string {
        return this.datePipe.transform(date, 'MM/dd/yyyy') || '';
    }

    formatPlotDate(date: Date): string {
        return this.datePipe.transform(date, 'MM/dd') || '';
    }

    get metricDirection(): string {
        if (!this.gameMetric || !this.gameMetric.MetricDetail) {
            return this.getLocalizedText("Down", "Down");
        }
        const direction = Number(this.gameMetric.MetricDetail.DefaultMetricDirection);
        return direction === 1 ? this.getLocalizedText("Up", "Up") : this.getLocalizedText("Down", "Down");
    }
}